import type { ExportCatalog } from './catalog.js';
import type { EnrichedExportNode } from './types.js';

export type ImportPlanOrderingItem = {
  notionId: string;
  parentNotionId: string | null;
};

/**
 * Nearest page ancestor in the export tree (skips database collection nodes).
 * Used to place imported databases and pages under the correct wiki page.
 */
export function findParentPageNotionId(
  notionId: string,
  roots: EnrichedExportNode[],
): string | null {
  function findAncestorPath(
    items: EnrichedExportNode[],
    ancestors: EnrichedExportNode[],
  ): EnrichedExportNode[] | null {
    for (const item of items) {
      if (item.notionId === notionId) {
        return ancestors;
      }
      const found = findAncestorPath(item.children, [...ancestors, item]);
      if (found) {
        return found;
      }
    }
    return null;
  }

  const ancestors = findAncestorPath(roots, []);
  if (!ancestors) {
    return null;
  }

  for (let i = ancestors.length - 1; i >= 0; i--) {
    const node = ancestors[i]!;
    if (node.kind === 'page') {
      return node.notionId;
    }
  }
  return null;
}

/** Page ancestors that must be imported so nested selections keep Notion hierarchy. */
export function requiredAncestorPageIds(
  catalog: ExportCatalog,
  selectedPageIds: Set<string>,
  selectedDatabaseIds: Set<string>,
): Set<string> {
  const importing = new Set([...selectedPageIds, ...selectedDatabaseIds]);
  const ancestors = new Set<string>();

  for (const notionId of [...selectedPageIds, ...selectedDatabaseIds]) {
    let parentId = findParentPageNotionId(notionId, catalog.enrichedRoots);
    while (parentId) {
      if (importing.has(parentId) || ancestors.has(parentId)) {
        break;
      }
      ancestors.add(parentId);
      parentId = findParentPageNotionId(parentId, catalog.enrichedRoots);
    }
  }

  return ancestors;
}

/** Import parents before children so server-side parent id resolution succeeds. */
export function sortByParentOrder<T extends ImportPlanOrderingItem>(items: T[]): T[] {
  const byNotionId = new Map(items.map((item) => [item.notionId, item]));
  const sorted: T[] = [];
  const visited = new Set<string>();

  function visit(item: T): void {
    if (visited.has(item.notionId)) {
      return;
    }
    if (item.parentNotionId) {
      const parent = byNotionId.get(item.parentNotionId);
      if (parent) {
        visit(parent);
      }
    }
    visited.add(item.notionId);
    sorted.push(item);
  }

  for (const item of items) {
    visit(item);
  }
  return sorted;
}
